import React, { memo } from 'react';
import { AppNode } from '@/types';
import { Image as ImageIcon2 } from 'lucide-react';
import { GridOverlay } from '../GridOverlay';
import { VideoProgressBar } from './VideoProgressBar';
import { NODE_TYPE_ICON_URLS } from '../NodeTitle';

export const MediaUploadContent: React.FC<{
  data: AppNode;
  hasMedia: boolean;
  gridSplit: { rows: number; cols: number } | null;
  activeFeatureGrid: { rows: number; cols: number } | null;
  selectedCells: Set<string>;
  onSelectedCellsChange: (cells: Set<string>) => void;
  mediaRef: React.RefObject<HTMLImageElement | HTMLVideoElement | HTMLAudioElement | null>;
  isHovered: boolean;
  selected: boolean;
  isPlayingAudio: boolean;
  setIsPlayingAudio: (v: boolean) => void;
  isPlayingVideo: boolean;
  videoProgress: number;
  videoDuration: number;
  videoVolume: number;
  isVideoMuted: boolean;
  hasAudioTrack: boolean;
  onToggleVideoPlay: () => void;
  onToggleVideoMute: () => void;
  onChangeVideoVolume: (volume: number) => void;
  onVideoTimeUpdate: () => void;
  onVideoSeek: (time: number) => void;
  onVideoEnded: () => void;
  onVideoLoadedMetadata: () => void;
  onExtractImage?: (nodeId: string, image: string) => void;
}> = memo(({
  data,
  hasMedia,
  gridSplit,
  activeFeatureGrid,
  selectedCells,
  onSelectedCellsChange,
  mediaRef,
  isHovered,
  selected,
  isPlayingAudio,
  setIsPlayingAudio,
  isPlayingVideo,
  videoProgress,
  videoDuration,
  videoVolume,
  isVideoMuted,
  hasAudioTrack,
  onToggleVideoPlay,
  onToggleVideoMute,
  onChangeVideoVolume,
  onVideoTimeUpdate,
  onVideoSeek,
  onVideoEnded,
  onVideoLoadedMetadata,
  onExtractImage,
}) => {
  if (!hasMedia) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {NODE_TYPE_ICON_URLS[data.type] ? (
          <img src={NODE_TYPE_ICON_URLS[data.type]} alt="" className="opacity-90" style={{ width: 48, height: 48 }} draggable={false} />
        ) : (
          <ImageIcon2 size={48} className="text-white/90" />
        )}
      </div>
    );
  }

  const showVideoControls = isHovered || selected;

  return (
    <>
      {data.data.image && (
        <img
          ref={mediaRef as any}
          src={data.data.image}
          className="w-full h-full object-cover bg-[#262626]"
          draggable={false}
        />
      )}
      {data.data.videoUri && (
        <div className="relative w-full h-full">
          <video
            key={data.data.videoUri}
            ref={mediaRef as any}
            data-video-node={data.id}
            src={data.data.videoUri}
            className="w-full h-full object-cover bg-[#262626]"
            loop
            playsInline
            preload="metadata"
            onTimeUpdate={onVideoTimeUpdate}
            onEnded={onVideoEnded}
            onLoadedMetadata={onVideoLoadedMetadata}
          />
          <VideoProgressBar
            nodeId={data.id}
            isPlaying={isPlayingVideo}
            progress={videoProgress}
            duration={videoDuration}
            volume={videoVolume}
            isMuted={isVideoMuted}
            hasAudioTrack={hasAudioTrack}
            visible={showVideoControls}
            onTogglePlay={onToggleVideoPlay}
            onToggleMute={onToggleVideoMute}
            onChangeVolume={onChangeVideoVolume}
            onSeek={onVideoSeek}
            onExtractImage={onExtractImage}
          />
        </div>
      )}
      {data.data.audioUri && (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-amber-900/10 z-0" />
          <audio
            ref={mediaRef as any}
            src={data.data.audioUri}
            onEnded={() => setIsPlayingAudio(false)}
            onPlay={() => setIsPlayingAudio(true)}
            onPause={() => setIsPlayingAudio(false)}
            className="hidden"
          />
        </div>
      )}
      {gridSplit && data.data.image && (
        <GridOverlay
          rows={gridSplit.rows}
          cols={gridSplit.cols}
          interactive
          selectedCells={selectedCells}
          onCellClick={(cellId) => {
            const next = new Set(selectedCells);
            if (next.has(cellId)) { next.delete(cellId); } else { next.add(cellId); }
            onSelectedCellsChange(next);
          }}
        />
      )}
      {activeFeatureGrid && (activeFeatureGrid.rows > 1 || activeFeatureGrid.cols > 1) && !gridSplit && (
        <GridOverlay rows={activeFeatureGrid.rows} cols={activeFeatureGrid.cols} />
      )}
    </>
  );
});
